import { addCosts, costEntries, CostType } from "./CaseConfiguration.ts";
import { NetResultStats } from "./CaseModel.ts";
import { CaseRealities } from "./PortfolioTypes.ts";

/**
 * Portfolio totals over the per-case two-realities snapshots: every case as it
 * resolved, with its settlements, against the same case fought to the end. Kept
 * apart from the page so the arithmetic can be unit-tested.
 */

/** One side of the comparison — a portfolio total over one world. */
export interface RealitySide {
  clientNet: number; // Σ client net result
  clientWorst: number; // Σ worst-case client net (adverse exposure)
  firmNet: number; // Σ law-firm net result (≈ margin), where it is known
  firmIncome: number; // Σ law-firm gross fee income
  costsByType: Partial<Record<CostType, number>>; // Σ client cost by type (negative)
  vat: number; // Σ VAT the client pays on those costs (negative)
}

const emptySide = (): RealitySide => ({
  clientNet: 0,
  clientWorst: 0,
  firmNet: 0,
  firmIncome: 0,
  costsByType: {},
  vat: 0,
});

const addWorld = (side: RealitySide, world: NetResultStats) => {
  side.clientNet += world.netResult.average;
  side.clientWorst += world.netResult.worst;
  // a firm without a fee structure has no known net result (see FirmCosts)
  if (world.lawFirm?.costs === "known") side.firmNet += world.lawFirm.netResult;
  side.firmIncome += world.lawFirm?.income ?? 0;
  addCosts(side.costsByType, world.costsByType ?? {});
  side.vat += world.vat ?? 0;
};

// every cost type either side carries, so a type present on one side only is
// still combined against zero rather than dropped
const costTypesOf = (...costs: RealitySide["costsByType"][]) =>
  new Set(costs.flatMap((c) => costEntries(c).map(([type]) => type)));

export interface TwoRealitiesOutcome {
  settled: RealitySide; // the cases as they resolved
  litigated: RealitySide; // the same cases had nobody settled
  avoidedCostByType: Partial<Record<CostType, number>>; // litigated − settled
  avoidedVat: number; // litigated − settled
  // cases left out of firmNet for want of a fee structure
  casesWithoutFeeStructure: number;
}

/** The costs a client can avoid by settling, in the order they are listed. */
const AVOIDABLE_COST_TYPES: CostType[] = [
  "lawFirmFee",
  "barristerFee",
  "courtFee",
  "expertFee",
  "otherDisbursement",
];

/**
 * What the clients did not have to spend because the cases settled, by cost
 * type, the VAT those who cannot reclaim it pay on the costs a line of its
 * own; a line of nothing is left out. `avoidedCostByType` is litigated −
 * actual and costs are negative, so what was avoided is its negation. The
 * law-firm-fee line is the firm's missed revenue rather than a pure saving.
 */
export function costsAvoided({
  avoidedCostByType,
  avoidedVat,
}: TwoRealitiesOutcome): { type: CostType | "vat"; value: number }[] {
  return [
    ...AVOIDABLE_COST_TYPES.map((type) => ({
      type,
      value: -(avoidedCostByType[type] ?? 0),
    })),
    { type: "vat" as const, value: -avoidedVat },
  ].filter(({ value }) => value !== 0);
}

/** Sum the per-case snapshots into the two portfolio-wide worlds. */
export function twoRealities(realities: CaseRealities[]): TwoRealitiesOutcome {
  const settled = emptySide();
  const litigated = emptySide();
  for (const reality of realities) {
    addWorld(settled, reality.actual);
    addWorld(litigated, reality.litigated);
  }

  const avoidedCostByType: Partial<Record<CostType, number>> = {};
  for (const type of costTypesOf(settled.costsByType, litigated.costsByType)) {
    avoidedCostByType[type] =
      (litigated.costsByType[type] ?? 0) - (settled.costsByType[type] ?? 0);
  }

  return {
    settled,
    litigated,
    avoidedCostByType,
    avoidedVat: litigated.vat - settled.vat,
    casesWithoutFeeStructure: realities.filter(
      ({ actual }) => actual.lawFirm?.costs === "unknown",
    ).length,
  };
}
